import { useCallback, useEffect, useRef, useState } from 'react'
import { Contract } from '@ethersproject/contracts'
import { ethers, BigNumber } from 'ethers'
import { BASE_CHAIN_ID_NUMBER, ETH_CHAIN_ID_NUMBER, MprolabContract } from '@mprolab/sdk'
import { useMproTokenContractAddress } from 'state/global/hooks'
import { getSimpleRpcProviderByChainId } from 'utils/providers'
import { tokenLogoSrc } from 'utils/tokenLogoUri'

const ERC20_MIN_ABI = [
  'function balanceOf(address owner) view returns (uint256)',
  'function decimals() view returns (uint8)'
]

export type WalletPanelBalanceRow = {
  id: string
  symbol: string
  chainLabel: string
  chainId: number
  amountDisplay: string
  /**
   * The read did not answer. Kept separate from the amount because a balance
   * of zero and a balance nobody could read are different facts, and printing
   * the second as the first is how this panel told a seller they had received
   * nothing minutes after 900 MPRO landed in their wallet.
   */
  failed: boolean
  /** ERC-20 address, so the panel can offer to add the token to the wallet. */
  tokenAddress?: string
  /**
   * The balance exactly as the chain holds it, in wei. `amountDisplay` is
   * rounded to five decimals for reading, so it must never be the source for
   * a Max: 900.000000002680615481 would send 900 and strand the rest, and a
   * value that rounded up would not send at all.
   */
  amountWei: string | null
  iconSrc: string
}

function pickActiveMpro(contracts: MprolabContract[] | undefined, chainId: number): string | undefined {
  return contracts?.find((c) => c.isActive && c.chainId === chainId)?.address
}

function formatAmountHuman(value: BigNumber, decimals: number): string {
  const s = ethers.utils.formatUnits(value, decimals)
  const n = parseFloat(s)
  if (!Number.isFinite(n)) return '0'
  if (n === 0) return '0'
  const rounded = Math.round(n * 100000) / 100000
  if (rounded === 0) return '0'
  return rounded.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 5
  })
}

/**
 * Balances for the wallet panel.
 *
 * `enabled` is what makes the numbers true rather than merely correct-once.
 * The panel renders `null` while closed but stays mounted, so a hook that
 * fetched only on mount kept serving whatever was true the first time the app
 * loaded. A seller who had just been paid 900 MPRO opened the panel and read
 * `0` — the read was never wrong, it was never repeated. Pass the panel's open
 * state and every opening re-reads the chain.
 */
export function useWalletPanelOnchainBalances(account: string | null | undefined, enabled = true) {
  const mproContracts = useMproTokenContractAddress()
  const [loading, setLoading] = useState(true)
  const [rows, setRows] = useState<WalletPanelBalanceRow[]>([])
  // Guards against a slow first read overwriting a newer one.
  const runIdRef = useRef(0)

  const fetchBalances = useCallback(async () => {
    if (!account) {
      setRows([])
      setLoading(false)
      return
    }

    const runId = ++runIdRef.current
    setLoading(true)

    const ethProvider = getSimpleRpcProviderByChainId(ETH_CHAIN_ID_NUMBER)
    const baseProvider = getSimpleRpcProviderByChainId(BASE_CHAIN_ID_NUMBER)
    const mproEth = pickActiveMpro(mproContracts, ETH_CHAIN_ID_NUMBER)
    const mproBase = pickActiveMpro(mproContracts, BASE_CHAIN_ID_NUMBER)

    /**
     * Every read is isolated. Previously one `await` sat outside its try, so a
     * single failing chain threw past all four rows and the whole panel was
     * written as zeros — a wallet with a balance on Base reported empty
     * because Ethereum was rate-limited.
     */
    const readNative = async (
      provider: ethers.providers.Provider
    ): Promise<{ text: string; failed: boolean; wei: string | null }> => {
      try {
        const v = await provider.getBalance(account)
        return { text: formatAmountHuman(v, 18), failed: false, wei: v.toString() }
      } catch {
        return { text: '—', failed: true, wei: null }
      }
    }

    const readErc20 = async (
      address: string | undefined,
      provider: ethers.providers.Provider
    ): Promise<{ text: string; failed: boolean; wei: string | null }> => {
      // No configured address is a known absence, not a failed read.
      if (!address) return { text: '0', failed: false, wei: '0' }
      try {
        const c = new Contract(address, ERC20_MIN_ABI, provider)
        let decimals = 18
        try {
          decimals = await c.decimals()
        } catch {
          decimals = 18
        }
        const v = await c.balanceOf(account)
        return { text: formatAmountHuman(v, decimals), failed: false, wei: v.toString() }
      } catch {
        return { text: '—', failed: true, wei: null }
      }
    }

    const [ethOne, ethBase, mproOne, mproOnBase] = await Promise.all([
      readNative(ethProvider),
      readNative(baseProvider),
      readErc20(mproEth, ethProvider),
      readErc20(mproBase, baseProvider)
    ])

    if (runId !== runIdRef.current) return

    setRows([
      {
        id: 'eth-1',
        symbol: 'ETH',
        chainLabel: 'Ethereum',
        chainId: ETH_CHAIN_ID_NUMBER,
        amountDisplay: ethOne.text,
        failed: ethOne.failed,
        amountWei: ethOne.wei,
        iconSrc: tokenLogoSrc('/images/tokens/eth.png')
      },
      {
        id: 'eth-base',
        symbol: 'ETH',
        chainLabel: 'Base',
        chainId: BASE_CHAIN_ID_NUMBER,
        amountDisplay: ethBase.text,
        failed: ethBase.failed,
        amountWei: ethBase.wei,
        iconSrc: tokenLogoSrc('/images/tokens/eth.png')
      },
      {
        id: 'mpro-1',
        symbol: 'MPRO',
        chainLabel: 'Ethereum',
        chainId: ETH_CHAIN_ID_NUMBER,
        amountDisplay: mproOne.text,
        failed: mproOne.failed,
        amountWei: mproOne.wei,
        tokenAddress: mproEth,
        iconSrc: tokenLogoSrc('/images/tokens/mpro.png')
      },
      {
        id: 'mpro-base',
        symbol: 'MPRO',
        chainLabel: 'Base',
        chainId: BASE_CHAIN_ID_NUMBER,
        amountDisplay: mproOnBase.text,
        failed: mproOnBase.failed,
        amountWei: mproOnBase.wei,
        tokenAddress: mproBase,
        iconSrc: tokenLogoSrc('/images/tokens/mpro.png')
      }
    ])
    setLoading(false)
  }, [account, mproContracts])

  useEffect(() => {
    if (!enabled) return
    void fetchBalances()
  }, [enabled, fetchBalances])

  return { rows, loading, refetch: fetchBalances }
}
